import { useEffect, useMemo, useState } from "react";
import { api } from "../api/apiClient";
import type {
  BulkCreateShiftsResponse,
  UserListEntry,
  VacationRequest,
} from "../api/types";
import { shiftPayField } from "../utils/shiftPayPayload";
import { parseFlexibleTime24 } from "../utils/time24";
import { parseOptionalTypedAmount } from "../utils/typedAmount";
import { AmountReadBack } from "./finances/AmountReadBack";
import { Modal } from "./Modal";
import { TimeInput24 } from "./TimeInput24";

interface BulkAddShiftsModalProps {
  locationId: string;
  defaultFrom: Date;
  defaultTo: Date;
  candidateUsers: UserListEntry[];
  initialUserId?: string;
  /** D-90: the server's `payVisible` — owners/admins set a pay rate; anyone else's form has no box and sends no key. */
  payVisible: boolean;
  onClose: () => void;
  onCreated: (result: BulkCreateShiftsResponse) => void;
}

const DAY_LABELS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 0, label: "Sun" },
];

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function BulkAddShiftsModal({
  locationId,
  defaultFrom,
  defaultTo,
  candidateUsers,
  initialUserId,
  payVisible,
  onClose,
  onCreated,
}: BulkAddShiftsModalProps) {
  const [userId, setUserId] = useState(
    initialUserId ?? candidateUsers[0]?.id ?? "",
  );
  const [fromDate, setFromDate] = useState(isoDate(defaultFrom));
  const [toDate, setToDate] = useState(isoDate(defaultTo));
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5, 6]);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");
  const [payRate, setPayRate] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vacations, setVacations] = useState<VacationRequest[]>([]);

  // G-8: approved vacations across the chosen range, for the soft warning
  // below. Markers only — a fetch failure never blocks bulk creation.
  useEffect(() => {
    if (!fromDate || !toDate) return;
    let stale = false;
    void api
      .listApprovedVacationsForLocation(locationId, { from: fromDate, to: toDate })
      .then((rows) => {
        if (!stale) setVacations(rows);
      })
      .catch(() => {
        if (!stale) setVacations([]);
      });
    return () => {
      stale = true;
    };
  }, [locationId, fromDate, toDate]);

  // Days the selected worker is on vacation AND would receive a shift
  // (inside the range, on a ticked weekday). Warn, never block.
  const vacationConflictDays = useMemo(() => {
    const conflicts: string[] = [];
    const mine = vacations.filter((v) => v.userId === userId);
    if (mine.length === 0 || !fromDate || !toDate) return conflicts;
    const rangeStart = new Date(`${fromDate}T00:00:00`);
    const rangeEnd = new Date(`${toDate}T00:00:00`);
    for (const vacation of mine) {
      const start = new Date(`${vacation.startDate.slice(0, 10)}T00:00:00`);
      const end = new Date(`${vacation.endDate.slice(0, 10)}T00:00:00`);
      const cursor = start < rangeStart ? new Date(rangeStart) : new Date(start);
      const stop = end > rangeEnd ? rangeEnd : end;
      for (; cursor <= stop; cursor.setDate(cursor.getDate() + 1)) {
        if (days.includes(cursor.getDay())) conflicts.push(isoDate(cursor));
      }
    }
    return [...new Set(conflicts)].sort();
  }, [vacations, userId, fromDate, toDate, days]);

  const canSubmit = useMemo(
    () =>
      Boolean(userId) &&
      Boolean(fromDate) &&
      Boolean(toDate) &&
      days.length > 0 &&
      Boolean(startTime) &&
      Boolean(endTime),
    [userId, fromDate, toDate, days, startTime, endTime],
  );

  const toggleDay = (value: number) => {
    setDays((prev) =>
      prev.includes(value)
        ? prev.filter((d) => d !== value)
        : [...prev, value],
    );
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const st = parseFlexibleTime24(startTime);
      const et = parseFlexibleTime24(endTime);
      if (!st || !et) {
        setError("Start and end must be valid 24-hour times (HH:mm).");
        setSubmitting(false);
        return;
      }
      const rateRead = parseOptionalTypedAmount(payRate);
      if (!rateRead.ok) {
        setError(`Pay rate: ${rateRead.message}`);
        setSubmitting(false);
        return;
      }
      const parsedRate = rateRead.value;
      const result = await api.bulkCreateShifts({
        locationId,
        userId,
        fromDate,
        toDate,
        daysOfWeek: days,
        startTime: st,
        endTime: et,
        ...shiftPayField(payVisible, parsedRate),
        notes: notes.trim() || undefined,
      });
      onCreated(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not create shifts");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Bulk add shifts" onClose={onClose}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-3)",
        }}
      >
        {error ? <div className="alert error">{error}</div> : null}

        <div className="field">
          <label htmlFor="bulk-user">Worker *</label>
          <select
            id="bulk-user"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          >
            {candidateUsers.length === 0 ? (
              <option value="">No eligible workers found</option>
            ) : (
              candidateUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fullName} · {u.role.toLowerCase()}
                </option>
              ))
            )}
          </select>
        </div>

        <div className="modal-form">
          <div className="field">
            <label htmlFor="bulk-from">From *</label>
            <input
              id="bulk-from"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="bulk-to">To *</label>
            <input
              id="bulk-to"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="bulk-start">Start *</label>
            <TimeInput24
              id="bulk-start"
              value={startTime}
              onChange={setStartTime}
              disabled={submitting}
            />
          </div>
          <div className="field">
            <label htmlFor="bulk-end">End *</label>
            <TimeInput24
              id="bulk-end"
              value={endTime}
              onChange={setEndTime}
              disabled={submitting}
            />
          </div>
          {payVisible ? (
            <div className="field">
              <label htmlFor="bulk-pay-rate">Pay rate</label>
              <input
                id="bulk-pay-rate"
                type="text"
                inputMode="decimal"
                value={payRate}
                onChange={(e) => setPayRate(e.target.value)}
                disabled={submitting}
              />
              <AmountReadBack raw={payRate} />
            </div>
          ) : null}
        </div>

        <div className="field">
          <label>Days of week *</label>
          <div className="dow-checkboxes">
            {DAY_LABELS.map((d) => (
              <label key={d.value}>
                <input
                  type="checkbox"
                  checked={days.includes(d.value)}
                  onChange={() => toggleDay(d.value)}
                />
                {d.label}
              </label>
            ))}
          </div>
        </div>

        <div className="field">
          <label htmlFor="bulk-notes">Notes</label>
          <input
            id="bulk-notes"
            type="text"
            placeholder="e.g. Weekend cover for May"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <small className="hint">
            Existing shifts that overlap will be skipped, not duplicated.
          </small>
        </div>

        {vacationConflictDays.length > 0 ? (
          <div className="vacation-banner">
            This worker has approved vacation on{" "}
            {vacationConflictDays.slice(0, 4).join(", ")}
            {vacationConflictDays.length > 4
              ? ` and ${vacationConflictDays.length - 4} more day(s)`
              : ""}{" "}
            inside this range — those days will be skipped automatically.
          </div>
        ) : null}

        <div className="button-row">
          <button type="button" className="button plain" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="button"
            onClick={() => void submit()}
            disabled={!canSubmit || submitting}
          >
            {submitting ? "Saving…" : "Create shifts"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
